/** The leaf value editor for a tag-data string — composes the content flags so a
 *  field can be multiline + Django-template + HTML at once. Used by both the
 *  non-i18n field path and each language leaf of a multilingual field, so every
 *  combination works identically.
 *
 *  Precedence: HTML wins the editing surface (a WYSIWYG ↔ raw-HTML toggle);
 *  otherwise template/multiline render a (monospace) textarea; else single-line.
 *  When a field is BOTH HTML and a Django template, raw mode lets you hand-write
 *  template tags precisely (WYSIWYG keeps them as literal text). */
import CodeIcon from '@mui/icons-material/Code';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { Box, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState } from 'react';

import { QuillEditor } from '@/components/RichText/QuillEditor';

export interface ValueFlags {
  html?: boolean;
  template?: boolean;
  multiline?: boolean;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
  flags?: ValueFlags;
  placeholder?: string;
}

export function TagValueInput({ value, onChange, readOnly = false, flags = {}, placeholder }: Props) {
  if (flags.html) {
    return <HtmlLeaf value={value} onChange={onChange} readOnly={readOnly} isTemplate={!!flags.template} />;
  }
  const monospace = !!flags.template;
  const multiline = !!flags.template || !!flags.multiline;
  return (
    <TextField
      size="small" fullWidth disabled={readOnly} placeholder={placeholder}
      multiline={multiline} minRows={multiline ? 3 : 1} maxRows={14}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={monospace ? { sx: { fontFamily: 'monospace' } } : undefined}
    />
  );
}

function HtmlLeaf({
  value,
  onChange,
  readOnly,
  isTemplate,
}: {
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  isTemplate: boolean;
}) {
  const [mode, setMode] = useState<'wysiwyg' | 'raw'>('wysiwyg');
  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
        <ToggleButtonGroup
          size="small" exclusive value={mode}
          onChange={(_, m) => { if (m) setMode(m as 'wysiwyg' | 'raw'); }}
        >
          <ToggleButton value="wysiwyg" sx={{ py: 0.25, px: 1 }}>
            <EditNoteIcon fontSize="small" sx={{ mr: 0.5 }} /> Visual
          </ToggleButton>
          <ToggleButton value="raw" sx={{ py: 0.25, px: 1 }}>
            <CodeIcon fontSize="small" sx={{ mr: 0.5 }} /> HTML
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {mode === 'wysiwyg' ? (
        <QuillEditor value={value} onChange={onChange} readOnly={readOnly} />
      ) : (
        <TextField
          size="small" fullWidth multiline minRows={4} maxRows={20} disabled={readOnly}
          value={value} onChange={(e) => onChange(e.target.value)}
          InputProps={{ sx: { fontFamily: 'monospace' } }}
          helperText={isTemplate ? 'Raw HTML — Django template tags are preserved as typed.' : 'Raw HTML source.'}
        />
      )}
    </Box>
  );
}
